import React, { useEffect, useRef } from "react";
import { FocusScope } from "react-aria";
import { t } from "ttag";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Clickable } from "../Clickables";
import { Svg } from "../Svg";
import { useHeaderContext } from "./HeaderContext";
import { MainMenuItem, SubmenuTrigger } from "./MainMenuItem";
import { SEARCH_SUBMENU, SearchSubmenu } from "./SearchSubmenu";
import { SubmenuPanel } from "./SubmenuPanel";

import themeStyles from "../../styles/themes-2026.module.scss";
import styles from "./MainNav.module.scss";

export const MainNav = () => {
    const {
        headerContent,
        activeSubmenu,
        isMobileLayout,
        setIsMobileLayout,
        isOverlapping,
        isAtTop,
        scrollDirection,
    } = useHeaderContext();

    const navRef = useRef<HTMLDivElement>(null);
    const logoRef = useRef<HTMLAnchorElement>(null);
    const menuItemsRef = useRef<HTMLDivElement>(null);
    const items = headerContent.main_nav.value;

    // Collision detection between logo and nav list
    useEffect(() => {
        const cb = () => {
            if (!menuItemsRef.current || !logoRef.current) return;

            const firstNavItem =
                menuItemsRef.current.querySelector("a, button");
            // This is a proper null check but we're rendering the search trigger
            // so functionally we shouldn't need to cover this
            /* v8 ignore if -- @preserve */
            if (!firstNavItem) return;

            const logoRect = logoRef.current.getBoundingClientRect();
            const firstItemRect = firstNavItem.getBoundingClientRect();
            const overlapping = logoRect.right + 10 > firstItemRect.left;

            setIsMobileLayout(overlapping);
        };
        const obs = new ResizeObserver(cb);

        /**
         * [Josh] could not find any combination of event listeners,
         * requestAnimationFrame, and requestIdleCallback that could replace
         * this setTimeout and have things work correctly.
         *
         * [Brendan] is pretty sure his lack of a deps array is load-bearing
         */
        setTimeout(cb, 100);
        obs.observe(navRef.current!);
        return () => obs.disconnect();
    });

    const isHidden = scrollDirection === "DOWN" && !isAtTop && !activeSubmenu;
    const isTransparent = isOverlapping && isAtTop && !activeSubmenu;

    return (
        <div
            ref={navRef}
            className={concatClassNames([
                styles.mainNav,
                isHidden ? styles.hidden : "",
                isTransparent
                    ? themeStyles.darkThemeText
                    : themeStyles.offWhite,
                isMobileLayout === true ? styles.hideDesktop : "",
                isMobileLayout === null ? styles.layoutUnset : styles.layoutSet,
            ])}
            data-overlapping={isOverlapping || undefined}
        >
            <div className={styles.inner}>
                <Clickable href="/" className={styles.logo} ref={logoRef}>
                    <Svg
                        name="sks-logo"
                        className={styles.logoSvg}
                        visuallyHiddenText={t`SKS: Home`}
                    />
                </Clickable>

                <FocusScope>
                    <div ref={menuItemsRef} className={styles.menuItems}>
                        {items.map((item) => (
                            <React.Fragment key={item.id}>
                                <MainMenuItem item={item} />
                                {item.type === "submenu" && (
                                    <SubmenuPanel
                                        menuId={item.id}
                                        items={item.value.items ?? []}
                                    />
                                )}
                            </React.Fragment>
                        ))}
                        <SubmenuTrigger menuId={SEARCH_SUBMENU} />
                        {!isMobileLayout && <SearchSubmenu />}
                    </div>
                </FocusScope>
            </div>
        </div>
    );
};
